import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { EntityConnectorInterface } from 'app/core/connector-services/entity-connector-service.interface';
import { RestConnectorService } from 'app/core/connector-services/rest-connector-service/rest-connector-service.abstract';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Notification, NotificationBulkSendInterface, NotificationInterface, NotificationTemplateInterface } from '../models';

import { NotificationServiceFilters } from './notification.service';

@Injectable()
export class NotificationRestConnectorService extends RestConnectorService
implements EntityConnectorInterface<NotificationInterface> {

  constructor(private readonly _httpClient: HttpClient,
              protected readonly _store: Store<AppStateInterface>) {
    super(_store);
  }

  public loadAll(notificationFilters?: Readonly<NotificationServiceFilters>): Observable<NotificationInterface[]> {
    if(notificationFilters) {
      return this.loadNotificationsForUser(notificationFilters.subjectId);
    }

    return this.loadNotificationsForUser(undefined);
  }

  public loadNotificationsForUser(userId?: number): Observable<NotificationInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    return this._httpClient.get<NotificationInterface[]>(
        `/api/users/${userIdFilter}/notifications`,
        { observe: 'response' },
      ).pipe(
        map((httpResponse: HttpResponse<NotificationInterface[]>) => httpResponse.body),
      );
  }

  public loadOne(id: number): Observable<NotificationInterface> {
    throw new Error('Not Implemented');
  }

  public updateManyReadUnread(notifications: NotificationInterface[], read:boolean): Observable<NotificationInterface[]> {
    const userIdList: number[] = notifications
    .filter((notification: NotificationInterface) => notification.subject.id === this._authenticatedUser$.value.id)
    .map((notification: NotificationInterface) => notification.subject.id);

    // This prevents updates by anyone other than the logged in user
    // Allows the notification card component to always fire update and not have to worry about who is opening
    let shouldUpdate: boolean = true;
    if(userIdList.length !== notifications.length) {
      shouldUpdate = false;
    }

    const requestPayload: { ids: number[], readState: boolean } = {
      readState: shouldUpdate ? read : false,
      ids: shouldUpdate ? notifications.map((notification: NotificationInterface) => notification.id) : [],
    };

    return this._httpClient.put<NotificationInterface>(
      `/api/users/${notifications[0].subject.id}/mark-read-unread`,
      requestPayload,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<any>) => {
        if(httpResponse.status !== 204) {
          return httpResponse.body;
        }
        const resultNotifications: NotificationInterface[] = shouldUpdate ?
        notifications.map((notification: NotificationInterface) => {
          return {
            ...notification,
            read,
          };
        }) : notifications;

        return resultNotifications;
      }),
    );
  }

  public update(notification: NotificationInterface): Observable<NotificationInterface> {
    const userIdFilter: number = notification.subject.id;

    // This prevents updates by anyone other than the logged in user
    // Allows the notification card component to always fire update and not have to worry about who is opening
    if(userIdFilter === this._authenticatedUser$.value.id) {
      return;
    }

    return this._httpClient.put<NotificationInterface>(
      `/api/users/${userIdFilter}/recommendations`,
      notification,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<NotificationInterface>) => {

        if(httpResponse.status !== 200) {
          return httpResponse.body;
        }
        const resultNotification: NotificationInterface = httpResponse.body;

        return new Notification(resultNotification);
      }),
  );
  }

  public loadNotificationTemplates(): Observable<Readonly<NotificationTemplateInterface[]>> {
    return this._httpClient.get<NotificationTemplateInterface[]>(
      'api/notifications',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<NotificationTemplateInterface[]>) => {
        return httpResponse.body;
      }),
    );
  }

  public sendBulkNotifications(sendNotificationData: NotificationBulkSendInterface): Observable<Readonly<number[]>> {
    return this._httpClient.post<NotificationBulkSendInterface>(
      'api/notifications/send',
      sendNotificationData,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<null>) => {
        if(httpResponse.status < 400) {
          return sendNotificationData.recipientIds;
        }
      }),
    );
  }

  public createNotificationTemplate(templateToCreate: NotificationTemplateInterface): Observable<Readonly<NotificationTemplateInterface>> {
    return this._httpClient.post<NotificationTemplateInterface>(
      'api/notifications',
      templateToCreate,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<NotificationTemplateInterface>) => {
        return httpResponse.body;
      }),
    );
  }

  public updateNotificationTemplate(templateToCreate: NotificationTemplateInterface): Observable<Readonly<NotificationTemplateInterface>> {
    return this._httpClient.put<NotificationTemplateInterface>(
      'api/notifications',
      templateToCreate,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<NotificationTemplateInterface>) => {
        return httpResponse.body;
      }),
    );
  }

  public deleteNotificationTemplate(templateToDelete: NotificationTemplateInterface): Observable<Readonly<NotificationTemplateInterface>> {
    return this._httpClient.delete(
      `/api/notifications/${templateToDelete.id}`,
      { observe: 'response' },
      ).pipe(
        map(() => {
          return templateToDelete;
        }),
      );
  }
}
